import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { hostSession, useStudioRooms, type StudioSession } from '../lib/studio/studio-api';
import { useMe } from '../lib/queries';
import { DeviceCheck } from '../components/studio/DeviceCheck';
import { StudioCall } from '../components/studio/StudioCall';
import { TakesList } from '../components/studio/TakesList';
import { ErrorMessage, Loading } from '../components/ui/Feedback';

/**
 * Debug switches: ?testSource=1 offers the test signal, ?relay=1 forces TURN,
 * ?simulateOffline=30 holds uploads for the first 30 s.
 */
export function studioFlags(search: string) {
  const q = new URLSearchParams(search);
  const offline = Number(q.get('simulateOffline'));
  return {
    testSource: q.get('testSource') === '1',
    relay: q.get('relay') === '1',
    simulateOfflineS: Number.isFinite(offline) && offline > 0 ? Math.min(offline, 600) : 0,
  };
}

export function StudioRoomPage() {
  const { roomId } = useParams<{ roomId: string }>();
  const { search } = useLocation();
  const flags = studioFlags(search);
  const navigate = useNavigate();
  const me = useMe();
  const rooms = useStudioRooms();
  const [session, setSession] = useState<StudioSession | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);

  useEffect(() => {
    let live = true;
    hostSession(roomId!).then(
      (s) => live && setSession(s),
      (err: unknown) => live && setError(err),
    );
    return () => {
      live = false;
    };
  }, [roomId]);

  if (error) return <ErrorMessage error={error} />;
  if (!session) return <Loading />;
  const room = rooms.data?.find((r) => r.id === roomId);
  const name = me.data?.user.email ?? 'Host';

  return (
    <div className="space-y-4">
      <nav className="text-sm text-muted">
        <Link to="/studio" className="hover:text-accent">
          ← Studio
        </Link>
      </nav>
      <h1 className="text-2xl font-semibold">{session.room.name}</h1>
      {!stream ? (
        <DeviceCheck name={name} allowTestSignal={flags.testSource} joinLabel="Enter studio" onJoin={(s) => setStream(s)} />
      ) : (
        <StudioCall
          session={session}
          displayName={name}
          stream={stream}
          isHost
          inviteUrl={room?.inviteUrl}
          relayOnly={flags.relay}
          simulateOfflineS={flags.simulateOfflineS}
          onLeave={() => navigate('/studio')}
        />
      )}
      <TakesList roomId={roomId!} />
    </div>
  );
}
